
import {
  Component, Watch, Mixins,
} from 'vue-property-decorator';
import { debounce } from 'ts-debounce';
import HistoryTable from '@/features/shipment-history/components/HistoryTable.vue';
import HistoryNoResults from '@/features/shipment-history/components/HistoryNoResults.vue';
import HistoryOrderModal from '@/features/shipment-history/components/HistoryOrderModal.vue';
import HistoryExportModal from '@/features/shipment-history/components/HistoryExportModal.vue';
import FiltersModal from '@/features/common/components/servicesFilter/FiltersModal.vue';
import DateRangePickerInput from '@/features/common/components/DateRangePickerInput.vue';
import { AvailableService, ShipStatus } from '@/features/shipment/types';
import { TabItem } from '@/features/common/components/BaseTabs.vue';
import { HistoryItemType, ShipmentSummaryItem } from '@/features/shipment-history/types';
import HistoryControls from '@/features/shipment-history/components/HistoryControls.vue';
import { getShipmentHistory, getShipmentsSummary } from '@/features/shipment-history/services/shipment-history.services';
import {
  deleteShipments, duplicateShipmentAction, getEstimate, reprintLabel, reschedulePickupAction,
} from '@/features/shipment-history/helpers';
import { modifyOrReturnShipment } from '@/features/shipment/helpers/createShipment';
import { fetchMoreUsingScrollWindow, showWarningAlertDialog } from '@/features/common/helpers';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import HistoryFilterButtons from '@/features/shipment-history/components/HistoryFilterButtons.vue';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import { hideFloatingWidgets, showFloatingWidgets } from '@/features/feedback-tool/helpers';

interface AdditionalFilters {
  meOnly: boolean,
  myAccount: boolean,
  deleted: boolean,
  todayOnly: boolean,
}

@Component({
  components: {
    HistoryTable,
    HistoryNoResults,
    HistoryOrderModal,
    HistoryExportModal,
    FiltersModal,
    HistoryControls,
    HistoryFilterButtons,
    DateRangePickerInput,
  },
})
export default class HistoryCard extends Mixins(ResponsivenessMixin) {
  private tabs: TabItem[] = [
    {
      icon: 'home-variant',
      iconColor: '#36134a',
      title: this.$t('history.tabNames.all') as string,
      to: { query: { status: 'all' } },
      name: 'all',
    },
    {
      icon: 'flag-variant',
      iconColor: '#623ec7',
      title: this.$t('history.tabNames.created') as string,
      to: { query: { status: ShipStatus.Created } },
      name: ShipStatus.Created,
    },
    {
      icon: 'truck-fast',
      iconColor: '#ffa43c',
      title: this.$t('history.tabNames.inTransit') as string,
      to: { query: { status: ShipStatus.InTransit } },
      name: ShipStatus.InTransit,
    },
    {
      icon: 'exclamation-thick',
      iconColor: '#cb0f51',
      title: this.$t('history.tabNames.exception') as string,
      to: { query: { status: ShipStatus.Exception } },
      name: ShipStatus.Exception,
    },
    {
      icon: 'check-bold',
      iconColor: '#2bc5cb',
      title: this.$t('history.tabNames.delivered') as string,
      to: { query: { status: ShipStatus.Delivered } },
      name: ShipStatus.Delivered,
    },
  ]

  private dateCalendar: string[] = [];

  private dateRangeInput = '';

  private showOrderModal = false;

  private showFiltersModal = false;

  private showExportModal = false;

  private selectedCarriers: string[] = [];

  private selectedItems: HistoryItemType[] = [];

  private isLoading = false;

  private requestCallCount = 1;

  private additionalFilters: AdditionalFilters = {
    deleted: false,
    meOnly: false,
    myAccount: false,
    todayOnly: false,
  }

  private tempAdditionalFilters: AdditionalFilters = { ...this.additionalFilters }

  private searchValue = '';

  private canFetchMore = true;

  private openedTrackingNumber = '';

  private fetchShipmentsLimit = 20;

  private shipmentsCount: ShipmentSummaryItem[] = [];

  get historyItems(): HistoryItemType[] {
    return this.$store.getters.getHistoryItems;
  }

  get showControls(): boolean {
    return !!this.selectedItems.length && !!this.historyItems.length;
  }

  get filterByStatus(): keyof typeof ShipStatus | 'all' {
    return ShipStatus[this.$route.query.status as string] || 'all';
  }

  get availableServices(): AvailableService[] {
    return this.$store.getters.getAvailableServices;
  }

  get onlyDeleteActionEnabled(): boolean {
    return this.selectedItems.length > 1;
  }

  get selectedItemsDeleted(): boolean {
    return this.selectedItems.every((shipment) => shipment.deleted);
  }

  get isMobile(): boolean {
    return this.$vuetify.breakpoint.mdAndDown;
  }

  private hideFloatingWidgets = hideFloatingWidgets;

  private showFloatingWidgets = showFloatingWidgets;

  @Watch('showControls')
  private hideShowFloatingWidgets() {
    if (this.showControls) {
      this.hideFloatingWidgets();
      return;
    }
    this.showFloatingWidgets();
  }

  beforeDestroy(): void {
    this.showFloatingWidgets();
  }

  @Watch('showFiltersModal')
  private resetAdditionalFilters() {
    this.tempAdditionalFilters = { ...this.additionalFilters };
  }

  @Watch('$route.query.status')
  private resetSelectedItems(): void {
    if (this.selectedItems.length) {
      this.toggleAllItems(false);
    }

    this.searchHandler();
  }

  async mounted(): Promise<void> {
    eventBus.$on(eventNames.SHIPMENTS_DELETED, async () => {
      this.selectedItems = [];
      await this.searchHandler();
    });

    eventBus.$on(eventNames.OPEN_HISTORY_DETAILS, ({ trackingNumber }) => {
      this.openedTrackingNumber = trackingNumber;
    });

    eventBus.$on(eventNames.CLOSE_HISTORY_DETAILS, () => {
      this.openedTrackingNumber = '';
    });

    this.isLoading = true;

    await this.searchHandler();

    window.addEventListener('scroll', this.windowScrollHandler);

    this.isLoading = false;
  }

  private destroyed(): void {
    window.removeEventListener('scroll', this.windowScrollHandler);
    eventBus.$off(eventNames.SHIPMENTS_DELETED);
    eventBus.$off(eventNames.OPEN_HISTORY_DETAILS);
    eventBus.$off(eventNames.CLOSE_HISTORY_DETAILS);
  }

  private async applyDateRangeFilter() {
    this.searchHandler();
  }

  private toggleItem(item: HistoryItemType): void {
    if (this.selectedItems.includes(item)) {
      const idx = this.selectedItems.indexOf(item);
      this.selectedItems.splice(idx, 1);
    } else {
      this.selectedItems.push(item);
    }
  }

  private toggleAllItems(value: boolean): void {
    if (value) {
      this.selectedItems = [...this.historyItems];
    } else {
      this.selectedItems = [];
    }
  }

  private applyFilters(): void {
    this.additionalFilters = { ...this.tempAdditionalFilters };
    this.showFiltersModal = false;
    this.canFetchMore = true;
    this.searchHandler();
  }

  private getShipmentsQtyByStatus(status: keyof typeof ShipStatus): number {
    return this.shipmentsCount.find((count) => count.status === status)?.count || 0;
  }

  private debouncedFetchMoreShipments = debounce(this.fetchMoreShipments, 200);

  private async fetchMoreShipments() {
    if (!this.canFetchMore) return;

    this.isLoading = true;

    const {
      myAccount, meOnly, todayOnly, deleted,
    } = this.additionalFilters;

    const items = await getShipmentHistory({
      Offset: this.fetchShipmentsLimit * this.requestCallCount,
      Limit: this.fetchShipmentsLimit,
      search: this.searchValue,
      carriers: this.selectedCarriers,
      fromDate: this.dateCalendar[0],
      toDate: this.dateCalendar[1],
      myaccount: myAccount || null,
      today: todayOnly || null,
      onlyMe: meOnly || null,
      deleted,
      statusTypes: this.filterByStatus === 'all' ? [] : [this.filterByStatus],
    });

    if (items?.length) {
      const finalItems = [...this.historyItems, ...items];

      this.$store.commit('setHistoryItems', finalItems);
      this.requestCallCount += 1;
    } else {
      this.canFetchMore = false;
    }

    this.isLoading = false;
  }

  private debouncedSearchHandler = debounce(this.searchHandler, 500);

  private async searchHandler(): Promise<void> {
    this.isLoading = true;
    this.selectedItems = [];

    const {
      myAccount, meOnly, todayOnly, deleted,
    } = this.additionalFilters;

    const items = await getShipmentHistory({
      Offset: 0,
      Limit: this.fetchShipmentsLimit,
      search: this.searchValue,
      carriers: this.selectedCarriers,
      fromDate: this.dateCalendar[0],
      toDate: this.dateCalendar[1],
      myaccount: myAccount || null,
      today: todayOnly || null,
      onlyMe: meOnly || null,
      deleted,
      statusTypes: this.filterByStatus === 'all' ? [] : [this.filterByStatus],
    });

    await this.getShipmentsCount();

    if (items) {
      this.$store.commit('setHistoryItems', items);
    }

    this.requestCallCount = 1;
    this.canFetchMore = items?.length === this.fetchShipmentsLimit;
    this.isLoading = false;
  }

  private async getShipmentsCount() {
    const {
      myAccount, meOnly, todayOnly, deleted,
    } = this.additionalFilters;

    const shipmentsSummary: ShipmentSummaryItem[] | null = await getShipmentsSummary({
      search: this.searchValue,
      carriers: this.selectedCarriers,
      fromDate: this.dateCalendar[0],
      toDate: this.dateCalendar[1],
      myaccount: myAccount || null,
      today: todayOnly || null,
      onlyMe: meOnly || null,
      deleted,
    });

    if (!shipmentsSummary) return;

    this.shipmentsCount = shipmentsSummary;
  }

  private windowScrollHandler(): void {
    const callback = () => {
      this.debouncedFetchMoreShipments();
    };
    fetchMoreUsingScrollWindow(callback);
  }

  private reprintLabel(): void {
    const { printSettings } = this.$store.getters.getUserPreferences;
    reprintLabel(this.selectedItems, printSettings.labelSize || 'Standard');
  }

  private editShipment(): void {
    modifyOrReturnShipment(
      this.selectedItems[0].trackingNumber,
      'modify',
    );

    this.selectedItems = [];
  }

  private returnShipment(): void {
    if (this.selectedItems[0]?.deleted) {
      showWarningAlertDialog(this.$t('history.alertDialog.cannotModifyDeletedShipment') as string);
      return;
    }

    modifyOrReturnShipment(
      this.selectedItems[0].trackingNumber,
      'return',
    );

    this.selectedItems = [];
  }

  private deleteShipments(): void {
    deleteShipments(this.selectedItems);
  }

  private getEstimate(): void {
    getEstimate(this.selectedItems[0]);
  }

  private duplicateShipment(): void {
    duplicateShipmentAction(this.selectedItems[0]);
  }

  private async reschedulePickup(): Promise<void> {
    const isSuccessful = await reschedulePickupAction(this.selectedItems[0]);
    if (!isSuccessful) return;

    eventBus.$emit(eventNames.CONTINUE_SCHEDULE_PICKUP, this.selectedItems[0]);
  }
}
